/**
 * Analyse stricte de la réponse de l'API Claude à l'étape TRANSFORMING (§5.1).
 *
 * Le modèle répond **uniquement** par un objet JSON décrivant la nature de
 * l'application, les modifications à appliquer à l'artifact, et les fichiers
 * éventuellement fournis en entier :
 * `{ "type": "react" | "static", "edits": [...], "files": { "chemin": "contenu" } }`.
 *
 * Ce module en fait la première barrière de la boucle de correction : toute
 * anomalie lève une `TransformParseError` dont le message est renvoyé au modèle
 * pour qu'il se corrige.
 *
 * **Le point d'entrée est garanti par construction** et non par un contrôle : il
 * est soit fourni en entier, soit matérialisé en appliquant les modifications à
 * l'artifact collé. Il n'existe pas de troisième voie, et les contrôles qui
 * vérifiaient sa présence ont donc disparu — une protection qui ne peut plus se
 * déclencher entretient une illusion de sûreté.
 */
import { z } from 'zod';
import { applyEdits } from './apply-edits.js';
import { MAX_FUNCTIONAL_CHANGE_LENGTH, MAX_FUNCTIONAL_CHANGES, MAX_TRANSFORMED_FILES, MAX_TRANSFORMED_TOTAL_BYTES, REACT_ENTRYPOINTS, STATIC_ENTRYPOINT, } from '../constants.js';
/** Nature de l'application produite par la transformation. */
export const TRANSFORMED_APP_TYPES = ['react', 'static'];
/**
 * Erreur d'analyse. Le message est rédigé pour être directement compréhensible
 * par le modèle : il décrit le problème et l'attendu.
 */
export class TransformParseError extends Error {
    code;
    /** Détails techniques additionnels (chemins fautifs, erreurs zod…). */
    details;
    constructor(code, message, details = []) {
        super(message);
        this.name = 'TransformParseError';
        this.code = code;
        this.details = details;
    }
    /**
     * Formate le retour envoyé au modèle dans le tour de correction suivant.
     * Reste en anglais : le prompt système de transformation l'est également, et
     * les modèles suivent mieux des consignes dans la langue du prompt.
     */
    /**
     * @param options.finalAttempt dernière tentative disponible : la porte de sortie
     *   du fichier complet n'est ouverte qu'à ce moment-là.
     */
    toModelFeedback(options = {}) {
        const detailBlock = this.details.length > 0 ? `\nDetails:\n- ${this.details.join('\n- ')}` : '';
        /**
         * Conduite à tenir après un échec d'ancrage, selon qu'il reste ou non des
         * tentatives.
         *
         * La porte de sortie — « renvoie le fichier complet » — était offerte dès le
         * premier échec. Le modèle la prenait aussitôt : un ancrage manqué suffisait à
         * faire réémettre vingt mille caractères, et les 98 % d'économie du mécanisme
         * de diff disparaissaient au premier accroc. Or un ancrage manqué est presque
         * toujours une citation approximative, qui se corrige en citant moins.
         *
         * L'issue certaine reste nécessaire — insister pourrait échouer autant de fois
         * que le budget le permet — mais elle n'est ouverte qu'à la dernière tentative.
         */
        /** Comment citer une ancre, quelle que soit la tentative. */
        const commentAncrer = [
            '- copy the text byte for byte from the artifact, indentation included;',
            '- quote **less**, not more: one distinctive line beats five approximate ones;',
            '- to add something at the very start or end of the file, use "prepend" or ' +
                '"append" — there is no text there to anchor to, and the delimiter lines around ' +
                'the artifact are not part of it.',
        ];
        const fichierCompletDisponible = options.fullFileAvailable ?? true;
        const conduite = !this.code.startsWith('edit_')
            ? []
            : options.finalAttempt && fichierCompletDisponible
                ? [
                    '',
                    'This is your last attempt. If you cannot reproduce the anchors exactly, stop ' +
                        'trying: return the complete entry point in "files" instead. That is always ' +
                        'accepted.',
                ]
                : options.finalAttempt
                    ? [
                        '',
                        'This is your last attempt. Returning the complete file is **not** an option ' +
                            'here: this artifact is too large to fit in the output limit, so the answer ' +
                            'would be cut off and discarded. Targeted "edits" are the only way through — ' +
                            'fix the anchor:',
                        ...commentAncrer,
                    ]
                    : [
                        '',
                        'Fix the anchor, do not give up on "edits" — you still have attempts left, and ' +
                            'sending the whole file back would discard every line you had correctly left ' +
                            'alone. A missed anchor is nearly always an approximate quote:',
                        ...commentAncrer,
                    ];
        return [
            `Your previous answer was rejected (${this.code}): ${this.message}${detailBlock}`,
            ...conduite,
            '',
            'Reply again with ONLY the JSON object, no markdown fence, no explanation.',
        ].join('\n');
    }
}
/** Schéma zod de la réponse attendue. */
const transformResponseSchema = z.object({
    type: z.enum(TRANSFORMED_APP_TYPES),
    /**
     * Modifications à appliquer à l'artifact pour obtenir le point d'entrée.
     * Absentes lorsque le modèle renvoie le fichier complet dans `files`.
     */
    edits: z
        .array(z.object({
        find: z.string(),
        replace: z.string(),
        all: z.boolean().optional(),
    }))
        .optional(),
    /**
     * Ajouts en tête et en pied du point d'entrée, hors mécanisme d'ancrage : il n'y
     * a pas de texte qui désigne la fin d'un fichier, et c'est là que l'ancrage
     * échouait le plus souvent.
     */
    prepend: z.string().optional(),
    append: z.string().optional(),
    /**
     * Fichiers fournis en entier : les modules créés de toutes pièces, et le point
     * d'entrée lui-même lorsque les modifications ciblées ne suffisent pas.
     */
    files: z.record(z.string(), z.string()).optional(),
    /** Le code produit appelle-t-il l'API de données ? Voir `needsDatabase`. */
    needsDatabase: z.boolean().optional(),
});
/**
 * Fichier que produisent les modifications ciblées, selon la nature de
 * l'application.
 *
 * C'est le fichier dont l'artifact collé constitue la version de départ : une
 * page pour un site statique, le composant racine pour une application React. Les
 * autres points d'entrée React (`src/App.tsx`) restent possibles, mais par
 * `files` : une base unique est ce qui permet de désigner « le fichier » sans
 * ambiguïté dans les modifications.
 */
export function editedEntrypoint(type) {
    return type === 'static' ? STATIC_ENTRYPOINT : REACT_ENTRYPOINTS[0];
}
/**
 * Extrait le point d'entrée d'un code transformé, tel qu'il est persisté.
 *
 * `transformedCode` porte la forme canonique `{ type, files }` produite par une
 * version. C'est **la sortie** de cette version — le code qui tourne réellement —
 * et donc le point de départ que doit recevoir la version suivante.
 *
 * Cette distinction a coûté une fonctionnalité en production. Une mise à jour
 * partait de `originalCode`, qui est *l'entrée* de la version précédente : la v3
 * démarrait donc sur le code de la v1, et le mode sombre ajouté par la v2 n'était
 * jamais montré au modèle. Il ne l'avait pas supprimé, il ne l'avait jamais vu —
 * chaque modification effaçait silencieusement la précédente.
 *
 * Défensive de bout en bout : un JSON invalide, une forme inattendue ou un objet
 * vide rendent `null`, et l'appelant se rabat sur ce qu'il a.
 */
export function extractEntrypoint(transformedCode) {
    if (!transformedCode)
        return null;
    let charge;
    try {
        charge = JSON.parse(transformedCode);
    }
    catch {
        return null;
    }
    if (typeof charge !== 'object' || charge === null)
        return null;
    const { type, files } = charge;
    if (typeof files !== 'object' || files === null)
        return null;
    const table = files;
    const candidats = type === 'static'
        ? [STATIC_ENTRYPOINT]
        : type === 'react'
            ? [...REACT_ENTRYPOINTS]
            : // Type inconnu : on tente les deux plutôt que d'abandonner. Une version
                // ancienne peut avoir été écrite avant que le champ n'existe.
                [STATIC_ENTRYPOINT, ...REACT_ENTRYPOINTS];
    for (const chemin of candidats) {
        const contenu = table[chemin];
        if (typeof contenu === 'string' && contenu.length > 0)
            return contenu;
    }
    // Aucun point d'entrée reconnu : le premier fichier non vide vaut mieux que
    // rien, et c'est presque toujours le bon dans une application d'un seul fichier.
    for (const contenu of Object.values(table)) {
        if (typeof contenu === 'string' && contenu.length > 0)
            return contenu;
    }
    return null;
}
/** Schéma d'un changement fonctionnel, appliqué entrée par entrée. */
const functionalChangeSchema = z.object({
    change: z.string().trim().min(1).max(MAX_FUNCTIONAL_CHANGE_LENGTH),
    reason: z.string().trim().min(1).max(MAX_FUNCTIONAL_CHANGE_LENGTH),
});
/**
 * Relève les changements fonctionnels déclarés, **sans jamais échouer**.
 *
 * C'est un choix délibéré, et l'inverse du reste de ce module. Le type et les
 * fichiers sont vérifiés strictement parce qu'ils conditionnent la construction :
 * une erreur y justifie de renvoyer le modèle à sa copie. Cette liste, elle, est
 * informative. Rejeter une transformation par ailleurs impeccable parce qu'un
 * libellé dépasse la longueur permise consommerait une tentative de correction —
 * et trois échecs de ce genre feraient échouer un déploiement pour une raison
 * qui n'a aucune incidence sur l'application produite.
 *
 * Les entrées inexploitables sont donc écartées une à une, et ce qui a été
 * écarté est retourné à l'appelant pour qu'il le journalise.
 */
export function extractFunctionalChanges(payload) {
    const brut = payload?.functionalChanges;
    if (brut === undefined || brut === null)
        return { changes: [], ignored: [] };
    if (!Array.isArray(brut)) {
        return { changes: [], ignored: ['« functionalChanges » n’est pas une liste : ignoré.'] };
    }
    const changes = [];
    const ignored = [];
    for (const [index, entree] of brut.entries()) {
        if (changes.length >= MAX_FUNCTIONAL_CHANGES) {
            ignored.push(`${brut.length - MAX_FUNCTIONAL_CHANGES} changement(s) au-delà de la limite de ` +
                `${MAX_FUNCTIONAL_CHANGES} : ignoré(s).`);
            break;
        }
        const result = functionalChangeSchema.safeParse(entree);
        if (result.success) {
            changes.push(result.data);
        }
        else {
            ignored.push(`changement n°${index + 1} inexploitable : ignoré.`);
        }
    }
    return { changes, ignored };
}
/**
 * Isole l'objet JSON d'une réponse brute : retire une éventuelle clôture
 * markdown, puis rogne tout ce qui précède la première accolade et suit la
 * dernière. Tolérant par pragmatisme, sans jamais accepter du non-JSON.
 */
export function extractJsonPayload(raw) {
    const trimmed = raw.trim();
    if (!trimmed) {
        throw new TransformParseError('empty_response', 'The response was empty.');
    }
    // Clôture markdown ```json … ``` ou ``` … ```
    const fenced = /```(?:json|jsonc|javascript|js)?\s*\n?([\s\S]*?)```/i.exec(trimmed);
    const candidate = (fenced?.[1] ?? trimmed).trim();
    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) {
        throw new TransformParseError('no_json_found', 'No JSON object was found in the response. The whole answer must be a single JSON object.');
    }
    return candidate.slice(start, end + 1);
}
/**
 * Vérifie qu'un chemin de fichier est sûr : relatif, POSIX, sans remontée de
 * répertoire ni segment suspect. Empêche l'écriture hors du répertoire de
 * travail lors du scaffolding.
 */
export function validateFilePath(path) {
    if (!path)
        return 'the path is empty';
    if (path.length > 200)
        return `the path is too long: ${path}`;
    if (path.startsWith('/'))
        return `absolute paths are not allowed: ${path}`;
    if (/^[a-zA-Z]:/.test(path))
        return `absolute paths are not allowed: ${path}`;
    if (path.includes('\\'))
        return `use forward slashes only: ${path}`;
    if (!/^[A-Za-z0-9._/-]+$/.test(path)) {
        return `the path contains unsupported characters: ${path}`;
    }
    const segments = path.split('/');
    if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
        return `the path must not contain empty or relative segments: ${path}`;
    }
    if (segments.some((segment) => segment.startsWith('.'))) {
        return `hidden files are not allowed: ${path}`;
    }
    return null;
}
/**
 * Analyse et valide intégralement une réponse du modèle.
 *
 * @throws TransformParseError si la réponse n'est pas exploitable
 */
export function parseTransformResponse(raw, options = {}) {
    const payload = extractJsonPayload(raw);
    let parsed;
    try {
        parsed = JSON.parse(payload);
    }
    catch (error) {
        throw new TransformParseError('invalid_json', 'The response is not valid JSON.', [
            error instanceof Error ? error.message : String(error),
        ]);
    }
    const result = transformResponseSchema.safeParse(parsed);
    if (!result.success) {
        throw new TransformParseError('schema_invalid', 'The JSON does not match the expected shape ' +
            '{ "type": "react" | "static", "edits": [ { "find": "…", "replace": "…" } ], "files": { "path": "content" } }.', result.error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`));
    }
    const { type } = result.data;
    const edits = result.data.edits ?? [];
    const wrap = {
        ...(result.data.prepend !== undefined ? { prepend: result.data.prepend } : {}),
        ...(result.data.append !== undefined ? { append: result.data.append } : {}),
    };
    const fournis = { ...(result.data.files ?? {}) };
    const entrypoint = editedEntrypoint(type);
    /**
     * Matérialisation du point d'entrée.
     *
     * Deux voies, exclusives l'une de l'autre. La normale : l'artifact collé sert de
     * base et les modifications s'y appliquent — ce qui garantit que tout ce que le
     * modèle n'a pas touché est identique au bit près à l'original. Le repli : le
     * fichier complet dans `files`, accepté sans réserve, car une réponse qui aboutit
     * vaut mieux qu'un ancrage défendu jusqu'à l'épuisement des tentatives.
     *
     * Les deux ensemble sont refusés : il faudrait choisir arbitrairement lequel fait
     * foi, et se tromper une fois sur deux.
     */
    /**
     * Le point d'entrée est-il déjà fourni en entier ?
     *
     * La question porte sur **tous** les points d'entrée admis et non sur le seul
     * fichier que visent les modifications : une application React peut être livrée
     * en `src/App.tsx`, et matérialiser un `src/App.jsx` à côté produirait deux
     * racines dont une seule serait compilée.
     */
    const fourniEnEntier = type === 'static'
        ? fournis[STATIC_ENTRYPOINT] !== undefined
        : REACT_ENTRYPOINTS.some((candidat) => fournis[candidat] !== undefined);
    const enveloppe = wrap.prepend !== undefined || wrap.append !== undefined;
    if (fourniEnEntier && (edits.length > 0 || enveloppe)) {
        throw new TransformParseError('entrypoint_conflict', 'You provided both a complete entry point in "files" and changes to apply to the ' +
            'artifact ("edits", "prepend" or "append"). Choose one.');
    }
    if (!fourniEnEntier) {
        if (options.originalCode === undefined) {
            // Erreur de programmation et non de modèle : ne pas la lui renvoyer.
            throw new Error(`parseTransformResponse : « ${entrypoint} » doit être matérialisé depuis les ` +
                'modifications, mais `originalCode` n’a pas été fourni.');
        }
        /**
         * Création à partir d'une description : il n'y a pas de fichier de départ, donc
         * rien à modifier.
         *
         * Sans ce refus explicite, les modifications s'appliquaient à une chaîne vide et
         * échouaient sur un « ancrage introuvable » — vrai mais incompréhensible, et qui
         * n'indiquait pas au modèle ce qu'on attendait de lui.
         */
        if (options.originalCode.length === 0) {
            throw new TransformParseError('edits_without_source', 'There is no existing file to modify: this application is being created from a ' +
                'description. Return the complete application in "files" instead — no "edits", no ' +
                '"prepend", no "append".', [`« ${entrypoint} » attendu dans "files"`]);
        }
        fournis[entrypoint] = applyEdits(options.originalCode, edits, wrap);
    }
    const files = fournis;
    const entries = Object.entries(files);
    // Aucun contrôle sur un ensemble vide : il ne peut pas l'être. Le point d'entrée
    // est soit fourni, soit matérialisé — jamais absent.
    if (entries.length > MAX_TRANSFORMED_FILES) {
        throw new TransformParseError('too_many_files', `Too many files: ${entries.length} (maximum ${MAX_TRANSFORMED_FILES}).`);
    }
    const pathProblems = entries
        .map(([path]) => validateFilePath(path))
        .filter((problem) => problem !== null);
    if (pathProblems.length > 0) {
        throw new TransformParseError('unsafe_path', 'Some file paths are not acceptable. Use relative POSIX paths such as "src/App.jsx".', pathProblems);
    }
    const emptyFiles = entries.filter(([, content]) => content.trim() === '').map(([path]) => path);
    if (emptyFiles.length > 0) {
        throw new TransformParseError('empty_file_content', 'Some files have empty content.', emptyFiles);
    }
    // `TextEncoder` plutôt que `Buffer` : ce module est aussi chargé par le front.
    const encoder = new TextEncoder();
    const totalBytes = entries.reduce((sum, [path, content]) => sum + encoder.encode(path).length + encoder.encode(content).length, 0);
    if (totalBytes > MAX_TRANSFORMED_TOTAL_BYTES) {
        throw new TransformParseError('too_large', `The generated files are too large: ${totalBytes} bytes (maximum ${MAX_TRANSFORMED_TOTAL_BYTES}).`);
    }
    // La présence du point d'entrée n'est plus vérifiée ici : elle est **garantie
    // par construction** quelques lignes plus haut. Il est soit fourni en entier par
    // le modèle, soit matérialisé depuis l'artifact — il n'existe pas de troisième
    // voie. Conserver le contrôle aurait laissé croire à une protection qui ne peut
    // plus se déclencher.
    // Relevée après les contrôles bloquants, et sans pouvoir les faire échouer.
    const { changes, ignored } = extractFunctionalChanges(parsed);
    for (const message of ignored) {
        options.onWarning?.(message);
    }
    return {
        type,
        files,
        ...(changes.length > 0 ? { functionalChanges: changes } : {}),
        responseShape: fourniEnEntier ? 'full-file' : 'edits',
        editCount: edits.length,
        needsDatabase: result.data.needsDatabase === true,
    };
}
//# sourceMappingURL=parse-response.js.map